import { useEffect, useState } from "react";
import { ApiRequest } from "GlobalFunctions";
import { useSnackbar } from "notistack";
import { 
    Dialog, 
    DialogTitle, 
    DialogContent, 
    DialogActions, 
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Checkbox,
    Typography,
    Box,
    InputLabel
} from "@mui/material";
import AutocompleteField_su from "elements/AutocompleteField_su";

interface TaxExemption {
    id: number;
    tax_type_id: number;
    tax_type_name: string;
    category_id: number;
    category_name: string;
    rate: number;
    location_level: string;
    is_exempt: boolean;
    exemption_id?: number;
    // Location fields from the tax_rate record
    country_id: number;
    region_id: number | null;
    city_id: number | null;
}

interface LocationTaxes {
    [key: string]: TaxExemption[];
}

export default function TaxSettingsEditor(props: any) {
    const { openEditor, setOpenEditor, setLoading, loadList } = props;
    const { enqueueSnackbar } = useSnackbar();
    
    const [selectedCountry, setSelectedCountry] = useState<any>(null);
    const [selectedRegion, setSelectedRegion] = useState<any>(null);
    const [selectedCity, setSelectedCity] = useState<any>(null);
    
    const [locationTaxes, setLocationTaxes] = useState<LocationTaxes>({});

    const [dropdownOpen, setDropdownOpen] = useState(false);

    /*=======================================
    HANDLE COUNTRY CHANGE
    =======================================*/
    const handleCountryChange = (data: any) => {
        console.log('Country selected:', data);
        setSelectedCountry(data);
        setSelectedRegion(null);
        setSelectedCity(null);
        setLocationTaxes({});
        
        if (data && data.value) {
            console.log('Loading taxes for country ID:', data.value);
            // Load taxes for country level
            loadTaxesForLocation(data.value);
        }
    };

    /*=======================================
    HANDLE REGION CHANGE  
    =======================================*/
    const handleRegionChange = (data: any) => {
        setSelectedRegion(data);
        setSelectedCity(null);
        
        if (data && data.value && selectedCountry) {
            loadTaxesForLocation(selectedCountry.value, data.value);
        }
    };

    /*=======================================
    HANDLE CITY CHANGE
    =======================================*/
    const handleCityChange = (data: any) => {
        setSelectedCity(data);
        
        if (data && data.value && selectedCountry && selectedRegion) {
            loadTaxesForLocation(selectedCountry.value, selectedRegion.value, data.value);
        }
    };

    /*==========================================
    LOAD TAXES FOR LOCATION
    ==========================================*/
    const loadTaxesForLocation = async (countryId?: string, regionId?: string, cityId?: string) => {
        if (!countryId) return;

        const store_id = localStorage.getItem('store_id');
        const params: any = {
            store_id: store_id,
            country_id: countryId
        };
        
        if (regionId) {
            params.region_id = regionId;
        }
        
        if (cityId) {
            params.city_id = cityId;
        }

        console.log('Loading taxes with params:', params);

        await ApiRequest({
            method: 'GET',
            url: '/taxes/exemptions/getTaxesForLocation',
            query: params,
            setResponse: (res: any) => {
                console.log('API response:', res);
                if (res.status === 'success') {
                    setLocationTaxes(res.data);
                } else {
                    console.error('Error loading taxes:', res.message || 'Unknown error');
                }
            }
        });
    };

    /*==========================================
    TOGGLE EXEMPTION
    ==========================================*/
    const toggleExemption = async (tax: TaxExemption, exempt: boolean) => {
        // Use the tax record's specific location data, not the form selection
        const store_id = localStorage.getItem('store_id');
        const params = {
            store_id: store_id,
            tax_type_id: tax.tax_type_id,
            country_id: tax.country_id,
            region_id: tax.region_id || null,
            city_id: tax.city_id || null,
            category_id: tax.category_id,
            exempt: exempt
        };

        console.log('Toggling exemption with params:', params);

        await ApiRequest({
            method: 'POST',
            url: '/taxes/exemptions/toggleExemption',
            body: params,
            setResponse: (res: any) => {
                console.log('Toggle exemption response:', res);
                if (res.status === 'success') {
                    enqueueSnackbar(res.message, { variant: "success" });
                    // Reload the taxes
                    if (selectedCountry) {
                        loadTaxesForLocation(
                            selectedCountry.value,
                            selectedRegion?.value,
                            selectedCity?.value
                        );
                    }
                } else {
                    enqueueSnackbar(res.message || 'Error updating exemption', { variant: "error" });
                }
            }
        });
    };

    /*==========================================
    HANDLE CLOSE
    ==========================================*/
    const handleClose = () => {
        setOpenEditor(false);
        setSelectedCountry(null);
        setSelectedRegion(null);
        setSelectedCity(null);
        setLocationTaxes({});
    };

    /*==========================================
    EFFECTS
    ==========================================*/
    useEffect(() => {
        if (selectedCountry) {
            loadTaxesForLocation(
                selectedCountry.value,
                selectedRegion?.value,
                selectedCity?.value
            );
        }
    }, [selectedCountry, selectedRegion, selectedCity]);

    /*==========================================
    FORMAT CATEGORY NAME - Convert key to display name
    ==========================================*/
    const formatCategoryName = (key: string): string => {
        // Convert snake_case to Title Case
        return key
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    /*==========================================
    RENDER TAX TABLE
    ==========================================*/
    const renderTaxTable = (taxes: TaxExemption[], categoryName: string) => {
        if (taxes.length === 0) return null;

        return (
            <Box sx={{ mb: 3 }}>
                <Typography variant="h6" sx={{ mb: 2 }}>
                    {categoryName}
                </Typography>
                <TableContainer className="table_1">
                    <Table size="small">
                        <TableHead>
                            <TableRow>
                                <TableCell>Tax Type</TableCell>
                                <TableCell>Rate</TableCell>
                                <TableCell>Level</TableCell>
                                <TableCell align="center">Exempt</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {taxes.map((tax) => (
                                <TableRow key={`${tax.tax_type_id}-${tax.category_id}`}>
                                    <TableCell>{tax.tax_type_name}</TableCell>
                                    <TableCell>{tax.rate}%</TableCell>
                                    <TableCell style={{ textTransform: 'capitalize' }}>
                                        {tax.location_level}
                                    </TableCell>
                                    <TableCell align="center">
                                        <Checkbox
                                            checked={tax.is_exempt}
                                            onChange={(e) => toggleExemption(tax, e.target.checked)}
                                        />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        );
    };

    return (
        <Dialog 
            open={openEditor} 
            onClose={handleClose}
            maxWidth="md"
            fullWidth
            PaperProps={{
                style: { 
                    borderRadius: '16px',
                    // Modify overflow behavior when dropdown is open
                    overflow: dropdownOpen ? 'visible' : 'auto',
                    // Ensure the dialog still has a defined height
                    maxHeight: 'calc(100% - 64px)'
                }
            }}
        >

            <div className="DialogContainer">
                <div className="ModuleWrapper">
                    <div className="boxContainerHeader">
                        <div className="dialog_container_title">
                            <h2 className="dialog_title">Tax Exemptions</h2>
                        </div>
                    </div>
                
                    <div className="dialogFormContainer">

                        <div className="form">
                            <div className="input_row">
                                    <div className="textfield_wrapper">
                                        <InputLabel className="external_label">Country</InputLabel>
                                        <AutocompleteField_su 
                                            endpoint="/countries/getCountriesV2"
                                            value={selectedCountry?.value || ''}
                                            className="autocomplete_small"
                                            onChange={handleCountryChange}
                                            fullWidth
                                            onOpen={() => setDropdownOpen(true)}
                                            onClose={() => setDropdownOpen(false)}
                                        />
                                    </div>

                                {selectedCountry && (
                                        <div className="textfield_wrapper">
                                            <InputLabel className="external_label">State/Region</InputLabel>
                                            <AutocompleteField_su 
                                                endpoint="/countries/getRegions"
                                                endpoint_params={{ country_id: selectedCountry.value }}
                                                value={selectedRegion?.value || ''}
                                                className="autocomplete_small"
                                                onChange={handleRegionChange}
                                                fullWidth
                                                disabled={!selectedCountry || !selectedCountry.value}
                                                onOpen={() => setDropdownOpen(true)}
                                                onClose={() => setDropdownOpen(false)}
                                            />
                                        </div>
                                )} 

                                {selectedRegion && (
                                        <div className="textfield_wrapper">
                                            <InputLabel className="external_label">City</InputLabel>
                                            <AutocompleteField_su
                                                endpoint="/shipping/zones/getCities"
                                                endpoint_params={{ region_id: selectedRegion.value }}
                                                max_results={10}
                                                dynamic={true}
                                                disabled={!selectedRegion || !selectedRegion.value}
                                                value={selectedCity?.value || ''}
                                                className="autocomplete_small"
                                                onChange={handleCityChange}
                                                clearOnSelect={true}
                                                fullWidth
                                                onOpen={() => setDropdownOpen(true)}
                                                onClose={() => setDropdownOpen(false)}
                                            />
                                        </div>
                                )}
                            </div>
                            
                                

                        {selectedCountry && (
                            <Box sx={{ mt: 3 }}>
                                <Typography variant="h5" sx={{ mb: 2 }}>
                                    Applicable Taxes
                                </Typography>
                                
                                {Object.keys(locationTaxes).map((categoryKey) => (
                                    renderTaxTable(locationTaxes[categoryKey], formatCategoryName(categoryKey))
                                ))}
                                
                                {Object.keys(locationTaxes).length === 0 && (
                                    <Typography color="text.secondary">
                                        No taxes found for the selected location.
                                    </Typography>
                                )}
                            </Box>
                        )}
                    </div>
                </div>
            </div>
        </div>
            

        </Dialog>
    );
}
